import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { InView } from 'react-intersection-observer'
import { Dropdown, DropdownContent, DropdownItem, SpinnerLoader } from 'shared/ui'
import { makeTextFieldIcon, TextField, TextFieldPlaceholder } from 'shared/ui/TextField'
import { type IResponseGetSurvey } from 'entities/Surveys/api/types'
import { createSurveySendSearchStore } from 'widgets/SurveySend/store/SurveySendSearchStore'
import styles from './styles.module.scss'

type ISurveySendModalSearchProps = {
  onSelect: (item: IResponseGetSurvey) => void
}

export const SurveySendModalSearch = observer(({ onSelect }: ISurveySendModalSearchProps) => {
  const [term, setTerm] = useState('')
  const ref = useRef<HTMLLabelElement | null>(null)
  const store = useMemo(() => createSurveySendSearchStore(), [])

  const [isActive, setActive] = useState(false)
  const [selection, setSelection] = useState<IResponseGetSurvey | null>(null)

  const handleLoadMore = useCallback(() => {
    const loadMore = isActive && !store.loading && store.hasMore
    if (loadMore) store.loadMore()
  }, [isActive, store.loading, store.hasMore])

  const handleSelect = useCallback(
    (item: IResponseGetSurvey) => {
      setActive(false)
      setTerm('')
      setSelection(item)
      onSelect(item)
    },
    [onSelect]
  )

  const isSearchLoading = store.termItemsEmpty && store.loading
  const isSearchHasMore = !store.termItemsEmpty && store.hasMore
  const isSearchNoResults = store.termItemsEmpty && !store.loading

  useEffect(() => {
    if (isActive) {
      store.loadMore()
    } else {
      store.reset()
      setTerm('')
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive) return
    store.searchItems(term)
  }, [term])

  return (
    <Dropdown
      show={isActive}
      onChangeOpen={setActive}
      placement='bottom-start'
      triggerComponent={() => (
        <TextFieldPlaceholder
          ref={ref}
          variant='stroke'
          size='medium'
          rightActions={[makeTextFieldIcon({ icon: 'chevronDown' })]}
          placeholder={
            selection ? (
              <div className={styles.dropdownSelection}>{selection.name}</div>
            ) : (
              <div className={styles.dropdownPlaceholder}>Select</div>
            )
          }
        />
      )}
      customComponent={() => (
        <DropdownContent
          noPadding
          width={ref.current?.offsetWidth}
          className={styles.dropdownContent}
        >
          <TextField
            size='medium'
            variant='integrated'
            InputProps={{ placeholder: 'Select' }}
            value={term}
            onChange={setTerm}
          />
          <div className={styles.dropdownList}>
            {store.initialLoading ? (
              isActive && (
                <SpinnerLoader
                  ariaLabel={'SurveySendModalSearch'}
                  className={styles.dropdownSpinner}
                />
              )
            ) : (
              <>
                {store.termItems.map((item) => (
                  <DropdownItem
                    key={item.id}
                    item={{ id: item.id, label: item.name }}
                    onChange={() => handleSelect(item)}
                  />
                ))}
                {isSearchLoading && (
                  <SpinnerLoader
                    ariaLabel={'SurveySendModalSearch'}
                    className={styles.dropdownSpinner}
                  />
                )}
                <InView onChange={(inView) => inView && handleLoadMore()}>
                  {({ ref: inViewRef }) => (
                    <div ref={inViewRef}>
                      {isSearchHasMore && (
                        <SpinnerLoader
                          ariaLabel={'SurveySendModalSearch'}
                          className={styles.dropdownSpinner}
                        />
                      )}
                      {isSearchNoResults && (
                        <div className={styles.dropdownNotFound}>No active surveys</div>
                      )}
                    </div>
                  )}
                </InView>
              </>
            )}
          </div>
        </DropdownContent>
      )}
    />
  )
})
